<template>
	<div v-if="model" :class="['subscription-form', { 'is-preview': preview }]">
		<!-- Payment method step -->
		<template v-if="step === 'payment'">
			<p>
				To upgrade to the
				<SubscriptionLevelName :level="newLevel" /> plan, please add your payment
				method below. You won't be charged until
				<template v-if="paymentIsImmediate"> you finalize the upgrade on the next screen</template><template v-else> {{ periodEndText }}</template>.
			</p>
			<slot name="payment" v-bind="scope" />
			<PaymentMethodForm verb="Add Payment Method" @save="onSavePaymentMethod">
				<template #actions>
					<IconLink icon="arrow-left" @click="preview = null">Back</IconLink>
				</template>
			</PaymentMethodForm>
		</template>
		<!-- END Payment method step -->

		<!-- Preview step -->
		<template v-else-if="step === 'preview'">
			<p>
				You have selected to
				<template v-if="billingPeriodChanged && !levelChanged">
					switch your current
					<SubscriptionLevelName :level="newLevel" /> plan from
					<strong>{{ oldBillingPeriod }}</strong> billing at
					<SubscriptionLevelPrice :level="newLevel" :period="oldBillingPeriod" :unit="oldBillingPeriod" />
					to <strong>{{ newBillingPeriod }}</strong> billing at
					<SubscriptionLevelPrice :level="newLevel" :period="newBillingPeriod" :unit="newBillingPeriod" />.
				</template>
				<template v-else>
					change your plan to
					<SubscriptionLevelName :level="newLevel" />.
				</template>
				This change will take effect
				<template v-if="changeIsImmediate">immediately</template><template v-else>{{ periodEndText }}</template><template v-if="paymentAmount">,
					<template v-if="!changeIsImmediate && !paymentIsImmediate">at which time</template>
					<template v-else>and</template>
					your payment method on file will
					<template v-if="!changeIsImmediate && paymentIsImmediate">
						<strong>immediately</strong>
					</template>
					be charged the cost difference of <strong>{{ formatCurrency(paymentAmount) }}</strong>
					<template v-if="changeIsImmediate && !paymentIsImmediate">
						{{ periodEndText }}</template></template>.
			</p>
			<slot name="preview" v-bind="scope" />

			<div class="flex-split wrap gap-16 mt-24">
				<button type="button" class="reset link gap-8" @click="preview = null">
					<svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" fill="none" viewBox="0 0 6 10">
						<g stroke="#004ADB" stroke-linecap="round" stroke-width="1.5" clip-path="url(#clip0_5795_139780)">
							<path d="M5 8.5 1.5 5M1.5 5 5 1.5" />
						</g>
						<defs>
							<clipPath id="clip0_5795_139780">
								<path fill="#fff" d="M0 .5h6v9H0z" />
							</clipPath>
						</defs>
					</svg>
					Back
				</button>
				<button class="btn btn-green btn-large" style="min-width:113px;" type="button" :disabled="isLoading" @click="onSubmit">Submit</button>
			</div>
		</template>
		<!-- END Preview step -->

		<!-- Plan selection step -->
		<template v-else>
			<slot name="plan" v-bind="scope" />
			<BaseForm v-bind="forward" @submit="loadPreview" :disabled="isLoading">
				<fieldset v-if="levels" class="subscription-form-levels form-section panel-checkboxes">
					<RadioField v-for="(currentLevel, index) in levels" :key="index" v-model="newLevel" :value="currentLevel">
						<template #label>
							<SubscriptionLevelName :level="currentLevel" />
							<SubscriptionLevelPrice :level="currentLevel" :period="newBillingPeriod" />
						</template>
						<template #description>
							<p>{{ currentLevel.description }}</p>
							<SubscriptionLevelDetails :level="currentLevel" class="button-inline" modal-class="subscription-form-level-benefits-modal">
								Learn More
							</SubscriptionLevelDetails>
						</template>
					</RadioField>
				</fieldset>
				<fieldset v-if="!newLevel.isFree" class="subscription-form-billing-period form-section">
					<legend>Billing Cycle:</legend>
					<div class="form-row">
						<RadioField v-model="newBillingPeriod" value="monthly" required>Monthly</RadioField>
						<RadioField v-model="newBillingPeriod" value="yearly" required>Yearly</RadioField>
					</div>
				</fieldset>
				<template #footer>
					<div class="flex-right">
						<button
							v-if="isDirty"
							type="submit"
							:disabled="isLoading"
							class="btn btn-green btn-large"
							style="min-width:110px">
							Next
						</button>
					</div>
				</template>
			</BaseForm>
		</template>
	</div>
</template>

<script>
import { DateTime } from 'luxon';
import { toBillingPeriod, toFiniteNumber } from '@/helpers/convert';
import { pick } from '@/helpers/reflect';
import objectForm from '@/mixins/objectForm';
import Subscription from '@/models/Subscription';
import SubscriptionLevel from '@/models/SubscriptionLevel';

import PaymentMethodForm from '@/components/forms/PaymentMethodForm';

export default {
  name: 'SubscriptionForm',
  mixins: [objectForm(Subscription)],
  components: { PaymentMethodForm },
  props: {
    verb: {
      default: null,
    },
  },
  data() {
    return {
      /** @type {BillingPeriod | null} */
      oldBillingPeriodData: null,
      /** @type {SubscriptionLevel | null} */
      oldLevelData: null,
      /** @type {SubscriptionUpdatePreview | null} */
      preview: null,
    };
  },
  computed: {
    /**
     * @returns {boolean}
     */
    billingPeriodChanged() {
      /** @type {SubscriptionLevel} */
      let newLevel;
      /** @type {SubscriptionLevel} */
      let oldLevel;
      return (
        this.newBillingPeriod !== this.oldBillingPeriod &&
        (!(oldLevel = this.oldLevel) ||
          !oldLevel.isFree ||
          !(newLevel = this.newLevel) ||
          !newLevel.isFree)
      );
    },
    /**
     * @returns {SubscriptionUpdatePreview['subscriptionAction'] | null}
     */
    change() {
      return this.preview?.subscriptionAction || null;
    },
    /**
     * @returns {boolean}
     */
    changeIsImmediate() {
      return !/Un?tilPeriodEnd/i.test(this.preview?.subscriptionAction);
    },
    /**
     * @returns {Intl.NumberFormat['format']}
     */
    formatCurrency() {
      return this.$getCurrencyFormat().format;
    },
    /**
     * @returns {Intl.DateTimeFormat['format']}
     */
    formatDate() {
      return this.$getDateTimeFormat(DateTime.DATE_FULL).format;
    },
    /**
     * @returns {boolean}
     */
    isDeactivated() {
      return !!this.subscription?.isDeactivated;
    },
    isDirty: {
      /**
       * @returns {boolean}
       */
      get() {
        return this.billingPeriodChanged || this.levelChanged;
      },
      /**
       * @returns {void}
       */
      set() {
        return;
      },
    },
    /**
     * @returns {boolean}
     */
    isDowngrade() {
      /** @type {SubscriptionUpdatePreview | null} */
      let preview;
      if (!this.levelChanged || !(preview = this.preview)) {
        return false;
      }
      return /^(cancel|downgrade)/i.test(preview.subscriptionAction);
    },
    /**
     * @returns {boolean}
     */
    isTrialing() {
      return !!this.subscription?.isTrialing;
    },
    /**
     * @returns {boolean}
     */
    isUpgrade() {
      /** @type {SubscriptionUpdatePreview | null} */
      let preview;
      if (!this.levelChanged || !(preview = this.preview)) {
        return false;
      }
      return /^upgrade/i.test(preview.subscriptionAction);
    },
    /**
     * @returns {boolean}
     */
    levelChanged() {
      return this.newLevel.roleId !== this.oldLevel.roleId;
    },
    /**
     * @returns {readonly SubscriptionLevel[] | null}
     */
    levels() {
      /** @type {readonly SubscriptionLevel[] | null} */
      const levels = this.$store.getters['subscriptions/levels'];
      if (!levels) {
        return null;
      }
      return levels.slice().sort((a, b) => {
        // If one of the levels is free, then the non-free level comes first
        if (a.isFree !== b.isFree) {
          return a.isFree ? 1 : -1;
        }
        // If the prices are different, then the cheaper level comes first
        if (a.yearlyPricePerMonth !== b.yearlyPricePerMonth) {
          return a.yearlyPricePerMonth - b.yearlyPricePerMonth;
        }
        // If the role IDs are different, the level with the lower role ID comes first
        if (a.roleId !== b.roleId) {
          return a.roleId - b.roleId;
        }
        return 0;
      });
    },
    /**
     * @returns {Subscription | null}
     */
    modelSource() {
      return null;
    },
    newBillingPeriod: {
      /**
       * @returns {BillingPeriod}
       */
      get() {
        const subscription = this.subscription;
        return (subscription && subscription.billingPeriod) || null;
      },
      /**
       * @param {BillingPeriod} value
       * @returns {void}
       */
      set(value) {
        const subscription = this.subscription;
        if (subscription) {
          subscription.billingPeriod = value;
        }
      },
    },
    newLevel: {
      /**
       * @returns {SubscriptionLevel}
       */
      get() {
        const subscription = this.subscription;
        return (
          (subscription && SubscriptionLevel.from(subscription.roleId)) || SubscriptionLevel.default
        );
      },
      /**
       * @param {SubscriptionLevel} value
       * @returns {void}
       */
      set(value) {
        const subscription = this.subscription;
        if (subscription) {
          subscription.roleId = (SubscriptionLevel.from(value) || SubscriptionLevel.default).roleId;
        }
      },
    },
    oldBillingPeriod: {
      /**
       * @returns {BillingPeriod}
       */
      get() {
        return toBillingPeriod(this.oldBillingPeriodData);
      },
      /**
       * @param {BillingPeriod} value
       * @returns {void}
       */
      set(value) {
        this.oldBillingPeriodData = value;
      },
    },
    oldLevel: {
      /**
       * @returns {SubscriptionLevel}
       */
      get() {
        return SubscriptionLevel.from(this.oldLevelData) || SubscriptionLevel.default;
      },
      /**
       * @param {SubscriptionLevel} value
       * @returns {void}
       */
      set(value) {
        this.oldLevelData = value;
      },
    },
    /**
     * @returns {number}
     */
    paymentAmount() {
      return toFiniteNumber(this.preview?.prorationPrice) || 0;
    },
    /**
     * @returns {boolean}
     */
    paymentIsImmediate() {
      const { preview, subscription } = this;
      if ((preview && preview.payImmediately) || !subscription) {
        return true;
      }
      if (subscription.status === 'trialing') {
        return subscription.trialDaysLeft <= 0;
      }
      const periodEnd = subscription.currentPeriodEndAt;
      return periodEnd == null || periodEnd <= Date.now();
    },
    paymentMethod: {
      /**
       * @returns {stripe.paymentMethod.PaymentMethod | null}
       */
      get() {
        return this.$store.getters['payments/paymentMethod'];
      },
      /**
       * @param {stripe.paymentMethod.PaymentMethod | null} paymentMethod
       */
      set(paymentMethod) {
        this.$store.commit('payments/setPaymentMethod', paymentMethod);
      },
    },
    /**
     * @returns {string}
     */
    periodEndText() {
      const subscription = this.subscription;
      /** @type {import('luxon').DateTime | null | undefined} */
      let endDate;
      let period = 'current billing cycle';
      let today = DateTime.now().startOf('day');
      if (subscription) {
        if (subscription.status === 'trialing') {
          period = 'free trial';
          const days = subscription.trialDaysLeft;
          if (days != null) {
            endDate = today.plus({ days });
          }
        } else {
          endDate = this.$toDate(subscription.currentPeriodEndAt);
        }
      }
      const afterPeriodEnds = `after your ${period} ends`;
      if (!endDate) {
        return afterPeriodEnds;
      }
      switch (endDate.startOf('day').diff(today.setZone(endDate.zone), 'days')) {
        case 0:
          return `${afterPeriodEnds} later today`;
        case 1:
          return `${afterPeriodEnds} tomorrow`;
      }
      return `${afterPeriodEnds} on ${this.formatDate(endDate.toMillis())}`;
    },
    /**
     * @returns {object}
     */
    scope() {
      return pick(this, [
        'billingPeriodChanged',
        'change',
        'changeIsImmediate',
        'isDeactivated',
        'isDowngrade',
        'isTrialing',
        'isUpgrade',
        'levelChanged',
        'newBillingPeriod',
        'newLevel',
        'oldBillingPeriod',
        'oldLevel',
        'paymentAmount',
        'paymentIsImmediate',
        'preview',
        'step',
      ]);
    },
    /**
     * @returns {'payment' | 'plan' | 'preview'}
     */
    step() {
      return this.preview
        ? this.newLevel.isFree || this.paymentMethod
          ? 'preview'
          : 'payment'
        : 'plan';
    },
    /**
     * @returns {Subscription | null}
     */
    subscription() {
      return this.model;
    },
  },
  methods: {
    /**
     * @returns {Promise<stripe.paymentMethod.PaymentMethod | null>}
     */
    async loadPaymentMethod() {
      let { paymentMethod } = this;
      if (!paymentMethod) {
        this.pendingActions++;
        try {
          paymentMethod = await this.$store.dispatch('payments/getMyPaymentMethod');
          this.paymentMethod = paymentMethod;
        } catch (e) {} // eslint-disable-line no-empty
        this.pendingActions--;
      }
      return paymentMethod;
    },
    /**
     * @returns {Promise<void>}
     */
    async loadPreview() {
      if (!this.isDirty) {
        return;
      }
      // If the new level isn't free, then we need to make sure the user has a payment method.
      if (!this.newLevel.isFree && !(await this.loadPaymentMethod())) {
        this.showPaymentForm = true;
      }
      this.preview = await this.action('subscriptions/updateMySubscriptionPreview', this.model);
    },
    /**
     * @param {stripe.paymentMethod.PaymentMethod} paymentMethod
     * @returns {void}
     */
    onSavePaymentMethod(paymentMethod) {
      this.paymentMethod = paymentMethod;
      this.$emit('save-payment-method', paymentMethod);
    },
    /**
     * @returns {Promise<void>}
     */
    async onSubmit() {
      this.clearMessages();
      try {
        // Update the subscription
        /** @type {Subscription} */
        const subscription = await this.action('subscriptions/updateMySubscription', this.model);
        this.$store.commit('subscriptions/setCurrent', subscription);
        this.save(subscription);
        this.$emit('save-subscription', subscription);
        this.successMessage = 'Your plan information was successfully saved.';
      } catch (error) {
        /** @type {readonly string[] | null | undefined} */
        const errors = this.errorMessage;
        if (!errors || !errors.length) {
          this.errorMessage = error || this.getDefaultErrorMessage();
        }
        throw error;
      } finally {
        this.preview = null;
      }
    },
  },
  async created() {
    this.$store.dispatch('subscriptions/getLevels');
    this.model = Subscription.create(await this.$store.dispatch('subscriptions/getMySubscription'));
  },
  watch: {
    /**
     * @param {Subscription | null} model
     */
    model(model) {
      if (model) {
        this.oldBillingPeriod = model.billingPeriod;
        this.oldLevel = SubscriptionLevel.from(model.roleId);
      } else {
        this.oldBillingPeriod = null;
        this.oldLevel = null;
      }
    },
    newBillingPeriod: {
      /**
       * @param {BillingPeriod} billingPeriod
       */
      handler(billingPeriod) {
        this.$emit('select-billing-period', billingPeriod);
      },
      immediate: true,
    },
    newLevel: {
      /**
       * @param {SubscriptionLevel} level
       */
      handler(level) {
        this.$emit('select-subscription-level', level);
      },
      immediate: true,
    },
    preview: {
      /**
       * @param {SubscriptionUpdatePreview | null} preview
       */
      handler(preview) {
        this.$emit('preview', preview);
      },
      immediate: true,
    },
    step: {
      /**
       * @param {'payment' | 'plan' | 'preview'}
       */
      handler(step) {
        this.$emit('change-step', step);
      },
      immediate: true,
    },
  },
};
</script>
